/**
 * Org session-policy toggle. Lives on Customer 360 admin_actions — that
 * tab is the successor of AccountDetail for org-bearing customers
 * (`/customers/accounts/:accountId` redirects here).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OpsApiError } from '../api/client.ts';
import {
  deleteOrgSessionPolicy,
  getOrgSessionPolicy,
  putOrgSessionPolicy,
} from '../api/sessionPolicy.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { useToast } from './Toast.tsx';
import { Button, Panel, Skeleton, Switch } from '../ui/index.ts';

function queryKey(orgId: string): readonly ['session-policy', string] {
  return ['session-policy', orgId] as const;
}

export function CustomerSessionPolicyCard({
  orgId,
  testId,
}: {
  readonly orgId: string;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: queryKey(orgId),
    queryFn: ({ signal }) => getOrgSessionPolicy(orgId, fetchImpl, signal),
    enabled: orgId.length > 0,
  });

  const putMut = useMutation({
    mutationFn: () => putOrgSessionPolicy(orgId, { session_policy: 'random' }, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKey(orgId) });
      toast.success(t('customer_manager_360.session_policy.saved'));
    },
    onError: (cause) => {
      toast.error(cause instanceof OpsApiError ? cause.message : t('customer_manager_360.session_policy.save_error'));
    },
  });

  const clearMut = useMutation({
    mutationFn: () => deleteOrgSessionPolicy(orgId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKey(orgId) });
      toast.success(t('customer_manager_360.session_policy.saved'));
    },
    onError: (cause) => {
      toast.error(cause instanceof OpsApiError ? cause.message : t('customer_manager_360.session_policy.save_error'));
    },
  });

  if (query.isPending) {
    return (
      <Panel caption={t('customer_manager_360.session_policy.caption')} padded testId={testId}>
        <Skeleton className="h-16" />
      </Panel>
    );
  }

  if (query.isError) {
    return (
      <Panel caption={t('customer_manager_360.session_policy.caption')} padded testId={testId}>
        <p className="m-0 text-dense text-bad-bright">{t('customer_manager_360.session_policy.error')}</p>
        <Button className="mt-3" onClick={() => void query.refetch()}>
          {t('customer_manager_360.session_policy.retry')}
        </Button>
      </Panel>
    );
  }

  const random = query.data.session_policy === 'random';
  const pending = putMut.isPending || clearMut.isPending;

  return (
    <Panel caption={t('customer_manager_360.session_policy.caption')} padded testId={testId}>
      <Switch
        checked={random}
        disabled={pending}
        testId={`${testId}-toggle`}
        label={random
          ? t('customer_manager_360.session_policy.random')
          : t('customer_manager_360.session_policy.content')}
        onCheckedChange={(checked) => {
          if (checked) putMut.mutate();
          else clearMut.mutate();
        }}
      />
      <p className="mt-3 m-0 text-dense text-paper-mid">
        {t('customer_manager_360.session_policy.hint')}
      </p>
    </Panel>
  );
}
